/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { useMediaQuery } from 'beautiful-react-hooks';

import { CMEQuestionnaireResponseTemplateType, CMEQuestionEnum, CMEQuestionsMap } from './cmeQuestionnaireData';
import { claimCMEThunk, selectClaimingCME, setActiveStep } from './cmeSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import styles from './CMEQuestionnaire.module.scss';

export default function CMEQuestionnaire() {
  const dispatch = useAppDispatch();
  const [values, setValues] = useState<CMEQuestionnaireResponseTemplateType[]>([
    {
      question: CMEQuestionEnum.IS_VALUE,
      answerYesOrNo: undefined,
      hideTextArea: true,
    },
    {
      question: CMEQuestionEnum.SCIENTIFICALLY_SOUND,
      answerYesOrNo: undefined,
      answerFreeText: '',
      hideTextArea: true,
    },

    {
      question: CMEQuestionEnum.BIAS,
      answerYesOrNo: undefined,
      answerFreeText: '',
      hideTextArea: true,
    },
    {
      question: CMEQuestionEnum.IMPROVE,
      answerYesOrNo: undefined,
      answerFreeText: '',
      hideTextArea: true,
    },

    {
      question: CMEQuestionEnum.IMPACT,
      answerYesOrNo: undefined,
      answerFreeText: '',
      hideTextArea: false,
    },
  ]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const isSubmitting = useAppSelector(selectClaimingCME);
  const handleChangeEvent = (e: React.SyntheticEvent, index: number) => {
    setValues((values) => {
      let newValues = [...values];
      const question = newValues[index];
      if (question) question.answerYesOrNo = (e.target as HTMLInputElement).value === 'true';
      return newValues as CMEQuestionnaireResponseTemplateType[];
    });
  };
  const handleSubmit = (e: any) => {
    try {
      e.preventDefault();
      //The hidetextarea is just to indicate when we want to hide the free text area ,we dont need that in payload
      values.forEach((question) => {
        delete question.hideTextArea;
      });
      void dispatch(claimCMEThunk({ questionnaire: values }));
    } catch (ex) {
      console.log(ex);
    }
  };
  function isFormValid(values: CMEQuestionnaireResponseTemplateType[]) {
    return values.every(
      (question) =>
        (question.answerYesOrNo !== undefined && question.answerYesOrNo === question.hideTextArea) ||
        (question.answerYesOrNo !== question.hideTextArea && question.answerFreeText?.trim() !== '')
    );
  }
  useEffect(() => {
    //Enable the submit button when the choices for all questions are marked and each text area that's visible has trimmed content
    //text area is rendered when answerYesOrNo!==hideTextArea
    setIsSubmitDisabled(!isFormValid(values));
  }, [values]);
  const showGrayedLine = useMediaQuery('(min-width:1000px)');
  const navigateBackToCMESelection = () => {
    dispatch(setActiveStep('selection'));
  };
  const handleSetFreeText = (e: React.SyntheticEvent, index: number) => {
    setValues((values) => {
      let newValues = [...values];
      const question = newValues[index];
      if (question) question.answerFreeText = (e.target as HTMLInputElement).value;
      return newValues as CMEQuestionnaireResponseTemplateType[];
    });
  };

  return (
    <form noValidate onSubmit={(e) => handleSubmit(e)} data-testid="cme-questionnaire">
      <div className={styles.container}>
        <div className={styles.questionnaire}>
          <div className={styles.questionnaireHeading}>Please answer the following questionnaire:</div>
          {values?.map((questionType, index) => (
            <div className={styles.questionGroup} key={index}>
              <div className={styles.questionGroupChoiceRow}>
                <p className={styles.question}>{CMEQuestionsMap.get(questionType.question)}</p>

                {showGrayedLine && (
                  <div
                    style={{
                      display: 'flex',
                      flexGrow: 1,
                      borderTop: '1px dashed  #E8EEF6',
                      marginTop: '20px',
                    }}
                  ></div>
                )}
                <div className={styles.choiceGroup}>
                  <RadioGroup
                    aria-labelledby="feedback-choices"
                    className={styles.radioGroup}
                    name="feedback-choices-group"
                    value={questionType.answerYesOrNo === undefined ? '' : questionType.answerYesOrNo.toString()}
                    onChange={(e) => handleChangeEvent(e, index)}
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '14px',
                        color: '#4C5771',
                      },
                    }}
                  >
                    <FormControlLabel
                      className={styles.choiceTypeLabel}
                      control={<Radio className={styles.radioBtn} />}
                      label="Yes"
                      value={'true'}
                      labelPlacement="start"
                      disabled={isSubmitting}
                    />
                    <FormControlLabel
                      value={'false'}
                      className={styles.choiceTypeLabel}
                      control={<Radio className={styles.radioBtn} />}
                      label="No"
                      labelPlacement="start"
                      disabled={isSubmitting}
                    />
                  </RadioGroup>
                </div>
              </div>
              {questionType.question !== CMEQuestionEnum.IS_VALUE && (
                <TextareaAutosize
                  minRows={5}
                  maxRows={5}
                  key={index}
                  className={
                    questionType.answerYesOrNo !== questionType.hideTextArea &&
                    !isSubmitting &&
                    questionType.answerYesOrNo !== undefined
                      ? styles.reasonTextArea
                      : styles.reasonTextAreaHidden
                  }
                  placeholder="Please explain"
                  disabled={isSubmitting}
                  value={questionType.answerFreeText || ''}
                  onChange={(e) => handleSetFreeText(e, index)}
                />
              )}
              <div></div>
            </div>
          ))}
        </div>
        <div className={styles.cmeButtons}>
          <Button
            sx={{ mr: 1 }}
            variant="contained"
            color="darkgray"
            disabled={isSubmitting}
            onClick={navigateBackToCMESelection}
          >
            Back
          </Button>

          <LoadingButton
            loading={isSubmitting}
            color="green"
            type="submit"
            disabled={isSubmitDisabled || isSubmitting}
            variant="contained"
          >
            Click here to initiate CME certification
          </LoadingButton>
        </div>
      </div>
    </form>
  );
}
